// src/components/resources/tools/generators/ScreenshotBeautifier/ScreenshotBeautifierContext.tsx

'use client'

import { createContext, useContext, useMemo, useReducer, useRef, type ReactNode } from 'react'
import { toast } from 'sonner'
import { captureAndDownload, captureToBlob } from '../shared/exportImage'
import {
  type ScreenshotBeautifierAction,
  type ScreenshotBeautifierActions,
  type ScreenshotBeautifierContextValue,
  type ScreenshotBeautifierState,
  type ScreenshotConfig,
  type ShadowLayer,
  type Preset
} from './ScreenshotBeautifier.types'
import { logClientError } from '~/lib/logger-client'


/** Default shadow layer for presets */
const DEFAULT_SHADOW: ShadowLayer = {
  id: '1',
  x: 0,
  y: 40,
  blur: 80,
  spread: -20,
  color: 'rgba(0,0,0,0.5)',
  enabled: true
}

/** Predefined style presets */
export const PRESETS: Preset[] = [
  {
    id: 'studio-glow',
    name: 'Studio Glow',
    emoji: '✨',
    config: {
      id: 'studio-glow',
      name: 'Studio Glow',
      padding: 60,
      borderRadius: 24,
      shadows: [DEFAULT_SHADOW],
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      backgroundBlur: 0,
      noise: { enabled: true, intensity: 20 },
      tilt: { x: 0, y: 0, enabled: false },
      filters: { brightness: 100, contrast: 100, saturate: 100, blur: 0 },
      border: { enabled: false, width: 4, color: '#ffffff' },
      scale: 1,
      deviceFrame: 'none',
      export: {
        format: 'png',
        quality: 'high',
        scale: 2,
        maintainAspectRatio: true,
        fileName: 'screenshot-beautified'
      },
      createdAt: new Date(),
      updatedAt: new Date()
    }
  },
  {
    id: 'glassmorphism',
    name: 'Glass',
    emoji: '🔮',
    config: {
      id: 'glassmorphism',
      name: 'Glass',
      padding: 80,
      borderRadius: 32,
      shadows: [
        { id: '1', x: 0, y: 20, blur: 60, spread: -10, color: 'rgba(0,0,0,0.2)', enabled: true },
        { id: '2', x: 0, y: 60, blur: 120, spread: -30, color: 'rgba(0,0,0,0.1)', enabled: true }
      ],
      background: 'linear-gradient(135deg, #8EC5FC 0%, #E0C3FC 100%)',
      backgroundBlur: 20,
      noise: { enabled: true, intensity: 8 },
      tilt: { x: 0, y: 0, enabled: false },
      filters: { brightness: 105, contrast: 95, saturate: 110, blur: 0 },
      border: { enabled: true, width: 2, color: 'rgba(255,255,255,0.5)' },
      scale: 1,
      deviceFrame: 'none',
      export: {
        format: 'png',
        quality: 'high',
        scale: 2,
        maintainAspectRatio: true,
        fileName: 'screenshot-beautified'
      },
      createdAt: new Date(),
      updatedAt: new Date()
    }
  },
  {
    id: 'minimal',
    name: 'Minimal',
    emoji: '⚪',
    config: {
      id: 'minimal',
      name: 'Minimal',
      padding: 40,
      borderRadius: 16,
      shadows: [{ id: '1', x: 0, y: 20, blur: 40, spread: 0, color: 'rgba(0,0,0,0.3)', enabled: true }],
      background: 'linear-gradient(135deg, #fafafa 0%, #e5e5e5 100%)',
      backgroundBlur: 0,
      noise: { enabled: false, intensity: 0 },
      tilt: { x: 0, y: 0, enabled: false },
      filters: { brightness: 100, contrast: 100, saturate: 100, blur: 0 },
      border: { enabled: false, width: 4, color: '#ffffff' },
      scale: 1,
      deviceFrame: 'none',
      export: {
        format: 'png',
        quality: 'high',
        scale: 2,
        maintainAspectRatio: true,
        fileName: 'screenshot-beautified'
      },
      createdAt: new Date(),
      updatedAt: new Date()
    }
  },
  {
    id: '3d-float',
    name: '3D Float',
    emoji: '🎲',
    config: {
      id: '3d-float',
      name: '3D Float',
      padding: 80,
      borderRadius: 28,
      shadows: [
        { id: '1', x: -20, y: 40, blur: 60, spread: -15, color: 'rgba(0,0,0,0.3)', enabled: true },
        { id: '2', x: 0, y: 80, blur: 120, spread: -40, color: 'rgba(0,0,0,0.2)', enabled: true }
      ],
      background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      backgroundBlur: 0,
      noise: { enabled: true, intensity: 12 },
      tilt: { x: 8, y: -12, enabled: true },
      filters: { brightness: 102, contrast: 105, saturate: 105, blur: 0 },
      border: { enabled: false, width: 4, color: '#ffffff' },
      scale: 0.95,
      deviceFrame: 'none',
      export: {
        format: 'png',
        quality: 'high',
        scale: 2,
        maintainAspectRatio: true,
        fileName: 'screenshot-beautified'
      },
      createdAt: new Date(),
      updatedAt: new Date()
    }
  },
  {
    id: 'neon',
    name: 'Neon',
    emoji: '💜',
    config: {
      id: 'neon',
      name: 'Neon',
      padding: 60,
      borderRadius: 20,
      shadows: [
        { id: '1', x: 0, y: 0, blur: 40, spread: 0, color: 'rgba(245, 87, 108, 0.6)', enabled: true },
        { id: '2', x: 0, y: 30, blur: 60, spread: -10, color: 'rgba(0,0,0,0.4)', enabled: true }
      ],
      background: 'linear-gradient(135deg, #12001a 0%, #1a0028 100%)',
      backgroundBlur: 0,
      noise: { enabled: true, intensity: 10 },
      tilt: { x: 0, y: 0, enabled: false },
      filters: { brightness: 110, contrast: 115, saturate: 130, blur: 0 },
      border: { enabled: true, width: 3, color: 'rgba(245, 87, 108, 0.8)' },
      scale: 1,
      deviceFrame: 'none',
      export: {
        format: 'png',
        quality: 'high',
        scale: 2,
        maintainAspectRatio: true,
        fileName: 'screenshot-beautified'
      },
      createdAt: new Date(),
      updatedAt: new Date()
    }
  },
  {
    id: 'app-store',
    name: 'App Store',
    emoji: '📱',
    config: {
      id: 'app-store',
      name: 'App Store',
      padding: 100,
      borderRadius: 40,
      shadows: [{ id: '1', x: 0, y: 50, blur: 100, spread: -20, color: 'rgba(0,0,0,0.4)', enabled: true }],
      background: 'linear-gradient(135deg, #FF6B6B 0%, #FF8E53 100%)',
      backgroundBlur: 0,
      noise: { enabled: false, intensity: 0 },
      tilt: { x: 0, y: 0, enabled: false },
      filters: { brightness: 100, contrast: 105, saturate: 110, blur: 0 },
      border: { enabled: false, width: 4, color: '#ffffff' },
      scale: 1,
      deviceFrame: 'none',
      export: {
        format: 'png',
        quality: 'high',
        scale: 2,
        maintainAspectRatio: true,
        fileName: 'screenshot-beautified'
      },
      createdAt: new Date(),
      updatedAt: new Date()
    }
  }
]

/**
 * Creates the default screenshot configuration used when the tool first loads.
 *
 * @returns {ScreenshotConfig} A fully typed default configuration object
 */
const createInitialConfig = (): ScreenshotConfig => {
  const now = new Date()

  return {
    id: 'screenshot-beautifier-default',
    name: 'Studio Glow',
    padding: 60,
    borderRadius: 24,
    shadows: [DEFAULT_SHADOW],
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    backgroundBlur: 0,
    noise: { enabled: true, intensity: 20 },
    tilt: { x: 0, y: 0, enabled: false },
    filters: { brightness: 100, contrast: 100, saturate: 100, blur: 0 },
    border: { enabled: false, width: 4, color: '#ffffff' },
    scale: 1,
    deviceFrame: 'none',
    export: {
      format: 'png',
      quality: 'high',
      scale: 2,
      maintainAspectRatio: true,
      fileName: 'screenshot-beautified'
    },
    createdAt: now,
    updatedAt: now
  }
}

/**
 * Creates the default application state for the Screenshot Beautifier.
 *
 * @returns {ScreenshotBeautifierState} The fully initialised state object
 */
const createInitialState = (): ScreenshotBeautifierState => ({
  image: null,
  config: createInitialConfig(),
  zoom: 0.7,
  showOriginal: false,
  copied: false,
  isProcessing: false,
  isExporting: false,
  error: null
})


/**
 * Reducer implementation powering the Screenshot Beautifier state machine.
 * Handles all supported actions defined in {@link ScreenshotBeautifierAction}.
 *
 * @param {ScreenshotBeautifierState} state - The current state snapshot
 * @param {ScreenshotBeautifierAction} action - The action to process
 * @returns {ScreenshotBeautifierState} The resulting state after applying the action
 */
const screenshotBeautifierReducer = (
  state: ScreenshotBeautifierState,
  action: ScreenshotBeautifierAction
): ScreenshotBeautifierState => {
  switch (action.type) {
    case 'SET_IMAGE':
      return {
        ...state,
        image: action.payload,
        error: null
      }

    case 'UPDATE_CONFIG':
      return {
        ...state,
        config: { ...state.config, ...action.payload, updatedAt: new Date() },
        error: null
      }

    case 'APPLY_PRESET':
      return {
        ...state,
        config: action.payload.config,
        error: null
      }

    case 'SET_ZOOM':
      return {
        ...state,
        zoom: action.payload
      }

    case 'TOGGLE_ORIGINAL':
      return {
        ...state,
        showOriginal: !state.showOriginal
      }

    case 'SET_COPIED':
      return {
        ...state,
        copied: action.payload
      }

    case 'ADD_SHADOW':
      return {
        ...state,
        config: {
          ...state.config,
          shadows: [...state.config.shadows, action.payload],
          updatedAt: new Date()
        }
      }

    case 'REMOVE_SHADOW':
      return {
        ...state,
        config: {
          ...state.config,
          shadows: state.config.shadows.filter(s => s.id !== action.payload),
          updatedAt: new Date()
        }
      }

    case 'UPDATE_SHADOW':
      return {
        ...state,
        config: {
          ...state.config,
          shadows: state.config.shadows.map(s =>
            s.id === action.payload.id ? { ...s, ...action.payload.updates } : s
          ),
          updatedAt: new Date()
        }
      }

    case 'SET_PROCESSING':
      return {
        ...state,
        isProcessing: action.payload
      }

    case 'SET_EXPORTING':
      return {
        ...state,
        isExporting: action.payload
      }

    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload
      }

    case 'RESET_STATE':
      return createInitialState()

    default:
      return state
  }
}

const ScreenshotBeautifierContext = createContext<ScreenshotBeautifierContextValue | undefined>(undefined)

/**
 * Creates memoised action helpers that wrap dispatch calls for ease-of-use within components.
 * These action creators provide type-safe interfaces for updating specific parts of the configuration
 * while handling the complexity of partial updates and nested object merging.
 *
 * @param {(action: ScreenshotBeautifierAction) => void} dispatch - Reducer dispatch function
 * @returns {ScreenshotBeautifierActions} Bound action helpers
 */
const createActions = (
  dispatch: (action: ScreenshotBeautifierAction) => void,
  getConfig: () => ScreenshotConfig,
  // Synchronous re-entrancy guard, shared with the rest of the generator family
  // (see `useExport`): a state read can't dedupe two same-frame clicks because
  // the `SET_EXPORTING` dispatch isn't committed before the handler's first
  // `await`. A ref closes that race.
  exportGuard: { current: boolean }
): ScreenshotBeautifierActions => ({
  setImage: (image: string | null) => dispatch({ type: 'SET_IMAGE', payload: image }),
  updateConfig: (config: Partial<ScreenshotConfig>) => dispatch({ type: 'UPDATE_CONFIG', payload: config }),
  applyPreset: (preset: Preset) => dispatch({ type: 'APPLY_PRESET', payload: preset }),
  setZoom: (zoom: number) => dispatch({ type: 'SET_ZOOM', payload: zoom }),
  toggleOriginal: () => dispatch({ type: 'TOGGLE_ORIGINAL' }),
  setCopied: (copied: boolean) => dispatch({ type: 'SET_COPIED', payload: copied }),
  addShadow: () => {
    const newShadow: ShadowLayer = {
      id: Date.now().toString(),
      x: 0,
      y: 20,
      blur: 40,
      spread: 0,
      color: 'rgba(0,0,0,0.3)',
      enabled: true
    }
    dispatch({ type: 'ADD_SHADOW', payload: newShadow })
  },
  removeShadow: (id: string) => dispatch({ type: 'REMOVE_SHADOW', payload: id }),
  updateShadow: (id: string, updates: Partial<ShadowLayer>) => 
    dispatch({ type: 'UPDATE_SHADOW', payload: { id, updates } }),
  reset: () => dispatch({ type: 'RESET_STATE' }),
  exportImage: () => {
    // Synchronous dedupe BEFORE the async work starts (matches every other
    // exporter's ref guard).
    if (exportGuard.current) return
    exportGuard.current = true
    void (async () => {
      try {
        dispatch({ type: 'SET_EXPORTING', payload: true })
        dispatch({ type: 'SET_ERROR', payload: null })

        // Find the preview container element
        const element = document.getElementById('screenshot-beautifier-preview')
        if (!element) {
          toast.error('Preview not available. Please ensure the preview is visible before exporting.')
          return
        }

        // Wait for any pending renders to complete
        await new Promise(resolve => setTimeout(resolve, 200))

        // Map the chosen export format to a 0..1 encoder quality value
        // (quality applies only to the lossy JPG/WebP encoders; PNG ignores
        // it). The shared helper handles MIME mapping, the JPG white matte,
        // the filename extension, and success/failure analytics. PDF is
        // intentionally NOT supported here.
        const currentConfig = getConfig()
        // Drive the export off the canonical `export.format` field. It is
        // typed `ImageFormat` ('png' | 'jpg' | 'jpeg' | 'webp' | 'pdf');
        // normalize 'jpeg' → 'jpg' and treat the unsupported 'pdf' as 'png'
        // so there is a single source of truth (no parallel `exportFormat`).
        const rawFormat = currentConfig.export?.format ?? 'png'
        const exportFormat: 'png' | 'jpg' | 'webp' =
          rawFormat === 'jpeg' || rawFormat === 'jpg' ? 'jpg' : rawFormat === 'webp' ? 'webp' : 'png'
        const qualityByPreset: Record<string, number> = {
          low: 0.6,
          medium: 0.8,
          high: 0.92,
          ultra: 1,
        }
        const quality = qualityByPreset[currentConfig.export?.quality ?? 'high'] ?? 0.92

        await captureAndDownload({
          element,
          format: exportFormat,
          filename: `adsights-screenshot-beautified-${Date.now()}`,
          quality,
          // Uses the default modern-screenshot engine. This tool was the one that
          // distorted under it (its preview's `transform: scale()` zoom on the
          // capture root made modern-screenshot bake the transform in and size to
          // the scaled box — squishing the export); `renderWithModernScreenshot`
          // now neutralizes a capture root's own transform and sizes to the
          // unscaled layout box, matching html2canvas. Verified on the real page
          // (default + 3D-tilt presets) — round aspect, tilt preserved.
          trackingSlug: 'screenshot-beautifier',
          onSuccess: () => toast.success('Screenshot exported successfully!'),
          onError: () => toast.error('Failed to export image. Please try again.'),
        })
      } finally {
        dispatch({ type: 'SET_EXPORTING', payload: false })
        exportGuard.current = false
      }
    })()
  },
  copyToClipboard: () => {
    void (async () => {
      try {
        dispatch({ type: 'SET_PROCESSING', payload: true })
        dispatch({ type: 'SET_ERROR', payload: null })
        
        // Find the preview container element
        const element = document.getElementById('screenshot-beautifier-preview')
        if (!element) {
          toast.error('Preview not available. Please ensure the preview is visible before copying.')
          return
        }
        
        // Wait for any pending renders to complete
        await new Promise(resolve => setTimeout(resolve, 200))

        // Capture through the SHARED pipeline (same engine, transform-root
        // neutralization, and html2canvas fallback as Export) so Copy and Export
        // produce pixel-identical PNGs from the same preview. Previously Copy used
        // raw html2canvas while Export used modern-screenshot — they could diverge.
        const result = await captureToBlob({ element, format: 'png' })
        if (!result) {
          toast.error('Failed to generate image. Please try again.')
          return
        }

        try {
          const clipboardItem = new ClipboardItem({ 'image/png': result.blob })
          await navigator.clipboard.write([clipboardItem])
          dispatch({ type: 'SET_COPIED', payload: true })
          toast.success('Screenshot copied to clipboard!')

          // Reset copied state after 2 seconds
          setTimeout(() => {
            dispatch({ type: 'SET_COPIED', payload: false })
          }, 2000)
        } catch (clipboardError) {
          logClientError(clipboardError, { component: 'ScreenshotBeautifierContext', context: 'Clipboard error' })
          toast.error('Failed to copy to clipboard. Your browser may not support this feature.')
        }
      } catch (error) {
        logClientError(error, { component: 'ScreenshotBeautifierContext', context: 'Copy error' })
        toast.error('Failed to copy image. Please try again.')
      } finally {
        dispatch({ type: 'SET_PROCESSING', payload: false })
      }
    })()
  },
  setProcessing: (value: boolean) => dispatch({ type: 'SET_PROCESSING', payload: value }),
  setExporting: (value: boolean) => dispatch({ type: 'SET_EXPORTING', payload: value }),
  setError: (message: string | null) => dispatch({ type: 'SET_ERROR', payload: message })
})

/**
 * Provider component that exposes the Screenshot Beautifier state and actions to the component tree.
 *
 * @param {object} props - Component props
 *   @param {ReactNode} props.children - Child nodes that require access to the context
 * @returns {JSX.Element} Provider element wrapping the children
 */
export const ScreenshotBeautifierProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  const [state, dispatch] = useReducer(screenshotBeautifierReducer, undefined, createInitialState)

  // Memoize actions to prevent unnecessary re-renders when dispatch doesn't change
  // The dispatch function is stable across renders, so this memoization is effective
  // Keep a live ref to the current config so action creators (which
  // close over `dispatch` only) can read the latest export format /
  // quality at export time without re-creating on every render.
  const configRef = useRef(state.config)
  // eslint-disable-next-line react-hooks/refs -- live ref to current config so the (event-time) action creators read the latest without re-creating each render
  configRef.current = state.config

  // Synchronous re-entrancy guard for the (event-time) export action — shared
  // with the rest of the generator family's `useExport` ref guard.
  const exportGuardRef = useRef(false)

  // eslint-disable-next-line react-hooks/refs -- the () => configRef.current closure is invoked at export (event) time, not during render
  const actions = useMemo(() => createActions(dispatch, () => configRef.current, exportGuardRef), [dispatch])

  // Memoize the context value to prevent unnecessary re-renders of consuming components
  // Only re-creates when state or actions change, optimizing performance
  const value = useMemo<ScreenshotBeautifierContextValue>(() => ({ state, actions }), [state, actions])

  return <ScreenshotBeautifierContext.Provider value={value}>{children}</ScreenshotBeautifierContext.Provider>
}

/**
 * Hook that returns the full Screenshot Beautifier context value.
 * Throws an error when accessed outside of the provider to guarantee predictable usage.
 *
 * @returns {ScreenshotBeautifierContextValue} Context value containing state and bound actions
 * @throws {Error} When used outside of ScreenshotBeautifierProvider
 */
export const useScreenshotBeautifier = (): ScreenshotBeautifierContextValue => {
  const context = useContext(ScreenshotBeautifierContext)
  if (!context) {
    throw new Error('useScreenshotBeautifier must be used within a ScreenshotBeautifierProvider')
  }

  return context
}

/**
 * Hook exposing only the Screenshot Beautifier state.
 *
 * @returns {ScreenshotBeautifierState} Current state snapshot
 */
export const useScreenshotBeautifierState = (): ScreenshotBeautifierState => {
  const { state } = useScreenshotBeautifier()
  return state
}

/**
 * Hook exposing only the action helpers to avoid unnecessary renders when only actions are required.
 *
 * @returns {ScreenshotBeautifierActions} Bound action helpers
 */
export const useScreenshotBeautifierActions = (): ScreenshotBeautifierActions => {
  const { actions } = useScreenshotBeautifier()
  return actions
}

/**
 * Hook that returns memoised helpers for common derived state calculations.
 * Additional derived selectors can be added here as functionality expands.
 *
 * @returns {{ hasImage: boolean, isBusy: boolean }} Derived booleans describing common UI states
 *   @returns {boolean} .hasImage - Whether a current image is loaded and available for processing
 *   @returns {boolean} .isBusy - Whether the application is currently processing or exporting
 */
export const useScreenshotBeautifierSelectors = (): { hasImage: boolean; isBusy: boolean } => {
  const { state } = useScreenshotBeautifier()

  // Calculate derived state values
  const hasImage = Boolean(state.image)
  const isBusy = state.isProcessing || state.isExporting

  // Memoize the selectors to prevent unnecessary re-renders when the derived values haven't changed
  return useMemo(() => ({ hasImage, isBusy }), [hasImage, isBusy])
}
